import React, { useEffect } from 'react';
import { CircularProgress, createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import gql from 'graphql-tag';
import { RouterHistoryProps, showNotification } from '../App';
import { client } from '..';
import { CalendlyConfigQuery } from './tabs/CalendlyTab';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            gap: theme.spacing(3),
            textAlign: 'center',
            padding: theme.spacing(4),
        },
    })
);

interface CalendlyAuthCallbackPageProps extends RouterHistoryProps {

}

const CALENDLY_SETTINGS_PATH = '/integrationConfiguration/CalendlyConfiguration';

// The authenticate mutation's own {error, status} tells us the request itself succeeded
// or failed immediately. This poll only covers the case where the request succeeded but
// isActive/webhook setup takes a while to finish server-side — give it up to 10 minutes
// (polling every 10s, not every 1s) before giving up and sending the user back.
const MAX_CONFIRM_ATTEMPTS = 60;
const CONFIRM_INTERVAL_MS = 10000;

export default function CalendlyAuthCallbackPage(props: CalendlyAuthCallbackPageProps) {

    const classes = useStyles();

    useEffect(() => {
        const searchParams = new URLSearchParams(props.history.location.search);
        const code = searchParams.get('code');
        const state = searchParams.get('state');

        if (!code || !state) {
            onFailed('Missing authorisation details from Calendly. Please try connecting again.');
            return;
        }

        client
            .mutate({
                mutation: CompleteCalendlyAuthMutation,
                variables: { code, state },
            })
            // tslint:disable-next-line: no-any
            .then((results: { data: any }) => {
                if (results.data) {
                    if (results.data.integrations.calendly.authenticate.status && results.data.integrations.calendly.authenticate.error === null) {
                        confirmActiveConnection(0);
                    } else {
                        onFailed(results.data.integrations.calendly.authenticate.error || 'Failed to complete Calendly authorisation. Please try again.');
                    }
                }
            })
            // tslint:disable-next-line: no-any
            .catch((reason: any) => {
                onFailed('Failed to complete Calendly authorisation. Please try again.', reason);
            });
    // tslint:disable-next-line: align
    }, []);

    const confirmActiveConnection = (attempt: number) => {
        client
            .query({
                query: CalendlyConfigQuery,
                fetchPolicy: 'network-only',
            })
            // tslint:disable-next-line: no-any
            .then((results: { data: any }) => {
                const isActive = results.data?.settings?.integrations?.calendly?.config?.isActive;
                if (isActive) {
                    showNotification(null, 'Calendly connected successfully', 'info');
                    props.history.push(CALENDLY_SETTINGS_PATH);
                    return;
                }
                retryOrGiveUp(attempt);
            })
            .catch(() => {
                retryOrGiveUp(attempt);
            });
    };

    const retryOrGiveUp = (attempt: number) => {
        if (attempt < MAX_CONFIRM_ATTEMPTS) {
            setTimeout(() => confirmActiveConnection(attempt + 1), CONFIRM_INTERVAL_MS);
        } else {
            onFailed('Calendly connection is taking longer than expected. Please try again.');
        }
    };

    // On failure, send the user straight back to the Calendly settings tab — it already
    // shows the "Connect to Calendly" call to action whenever the integration isn't
    // active, so there's no need for a separate reconnect screen here.
    // tslint:disable-next-line: no-any
    const onFailed = (message: string, reason?: any) => {
        showNotification('Failed to connect Calendly', reason ?? message, 'error');
        props.history.push(CALENDLY_SETTINGS_PATH);
    };

    return (
        <div className={classes.root}>
            <CircularProgress size={56} />
            <Typography variant="h6">Connecting to Calendly…</Typography>
            <Typography variant="body2" color="textSecondary">
                Please wait while we finish setting up your connection.
            </Typography>
        </div>
    );
}

const CompleteCalendlyAuthMutation = gql`
    mutation CompleteCalendlyAuth($code: String, $state: String) {
        integrations {
            calendly {
                authenticate(code: $code, state: $state) {
                    error
                    status
                }
            }
        }
    }
`;
